import React, { useEffect, useState } from 'react';
import {
  VStack,
  Heading,
  Text,
  Button,
  Box,
  Flex,
  useBreakpointValue,
  Link,
} from 'native-base';
import { useAffiliate } from '../../api/affiliates/useAffiliate';
import { HtmlRenderer } from './HtmlRenderer';
import OnboardingIntroMedia from './OnboardingIntroMedia';
import EnterpriseCobranding from './EnterpriseCobranding';
import { FORM_ACTIONS } from '../../utils/formActions';
import { BulletImage } from '../ui/BulletImage';
import styled from '@emotion/styled';
import LoginModal from '../LoginModal/LoginModal';

const DEFAULT_HEADER_TEXT = 'Your digital guide to better gut health';
const DEFAULT_SUBHEADER_TEXT = 'Find digestive relief today!';

const bubbleImg = './../mind-gut-i-darker.png';
const notesImg = './../notes.png';
const joyImg = './../joy.png';
const foodsImg = './../foods.png';
const shakingHandsImg = './../hands_shaking.png';
const mobilePhoneImg = './../mobile_phone.png';

const TOMATO = '#f15b33';
const MEDIUM_TURQUOISE = '#56cec6';
const GOLD = '#f7d31b';

const Item = styled('li')`
  align-items: flex-start;
  color: ${(p) => p.theme.colors.secondary['500']};
  display: flex;
  margin-top: 16px;
`;

const ListItem = ({ bgColor, bgSize, children, fontSize, img }) => {
  return (
    <Item data-testId="enterprise-signup-list-item">
      <BulletImage
        width={40}
        height={40}
        bgColor={bgColor}
        bgSize={bgSize}
        imgUrl={img}
      />
      <Heading
        alignSelf={'center'}
        color={'secondary.300'}
        fontSize={fontSize}
        ml={3}
        textAlign={'left'}
      >
        {children}
      </Heading>
    </Item>
  );
};

function EnterpriseSignupIntro({ dispatch, setParentPage }) {
  const { affiliate } = useAffiliate();
  const [showLogin, setShowLogin] = useState(false);

  const flexDir = useBreakpointValue({
    base: 'column',
    md: 'column',
    lg: 'row',
  });

  useEffect(() => {
    if (!!affiliate) {
      dispatch({
        type: FORM_ACTIONS.INIT_AFFILIATE,
        payload: affiliate,
      });
    }
  }, [affiliate, dispatch]);

  const Header = () => {
    const headerText = affiliate?.header || DEFAULT_HEADER_TEXT;

    return (
      <Heading
        display={'block'}
        fontSize={
          flexDir === 'column' ? '3xl' : ['2xl', '4xl', '4xl', '4xl', '5xl']
        }
        mt={6}
        mb={2}
        maxW={'550px'}
        textAlign={'left'}
      >
        {headerText}
      </Heading>
    );
  };

  const SubHeader = () => {
    const subHeaderText = affiliate?.subheader || DEFAULT_SUBHEADER_TEXT;

    return (
      <Text
        textAlign={'center'}
        fontSize={
          flexDir === 'column' ? 'xl' : ['xl', '2xl', '2xl', '2xl', '3xl']
        }
        mt={2}
      >
        {subHeaderText}
      </Text>
    );
  };

  const listItemFontSize =
    flexDir === 'column' ? 'lg' : ['lg', 'xl', 'xl', 'xl', '2xl'];

  const Intro = () => {
    return (
      <VStack ml={10} flexShrink={'initial'} style={{ alignItems: 'flex-end' }}>
        <Box display={'block'} w={'auto'}>
          <EnterpriseCobranding affiliate={affiliate} />
          <Header />
          <SubHeader />

          <Box mt={4} maxW={'600px'}>
            <Text fontSize={'lg'} lineHeight={24}>
              <HtmlRenderer html={affiliate?.body_text} />
            </Text>
          </Box>

          <Box style={{ marginBottom: '24px' }}>
            <Flex alignItems={'flex-start'} justifyContent={'center'}>
              <Button
                my={5}
                onPress={() => setParentPage(1)}
                testID={'onboarding-signup-button'}
              >
                Get Started
              </Button>
              <Text fontSize={16}>
                Already have an account?{' '}
                <Link
                  _text={{ textTransform: 'none' }}
                  onPress={() => setShowLogin(true)}
                >
                  Log in
                </Link>
              </Text>
            </Flex>

            <ul style={{ listStyleType: 'none', paddingLeft: '12px' }}>

            <ListItem
                bgColor={GOLD}
                fontSize={listItemFontSize}
                img={mobilePhoneImg}
              >
                <Text fontWeight={'medium'}>Access your </Text>
                <Text fontWeight={'bold'}>expert digestive care team </Text>
                <Text fontWeight={'medium'}>
                  anytime, from anywhere
                </Text>
              </ListItem>

              <ListItem
                bgColor={TOMATO}
                bgSize={'75%'}
                fontSize={listItemFontSize}
                img={notesImg}
              >
                <Text fontWeight={'bold'}>Find your trigger foods </Text>
                <Text fontWeight={'medium'}>and build a diet </Text>
                <Text fontWeight={'medium'}>
                  that works for you
                </Text>
              </ListItem>

              <ListItem
                bgColor={MEDIUM_TURQUOISE}
                fontSize={listItemFontSize}
                img={bubbleImg}
              >
                <Text fontWeight={'medium'}>Learn how to </Text>
                <Text fontWeight={'bold'}>
                  break the stress-symptom cycle
                </Text>
              </ListItem>

              <ListItem
                bgColor={TOMATO}
                bgSize={'50%'}
                fontSize={listItemFontSize}
                img={joyImg}
              >
                <Text fontWeight={'bold'}>Track your symptoms </Text>
                <Text fontWeight={'medium'}>
                  and see how they change over time
                </Text>
              </ListItem>

              <ListItem
                bgColor={MEDIUM_TURQUOISE}
                bgSize={'75%'}
                fontSize={listItemFontSize}
                img={foodsImg}
              >
                <Text fontWeight={'medium'}>Use our food scanner </Text>
                <Text fontWeight={'bold'}>
                  to navigate dietary restrictions
                </Text>
              </ListItem>

              <ListItem
                bgColor={GOLD}
                bgSize={'60%'}
                fontSize={listItemFontSize}
                img={shakingHandsImg}
              >
                <Text fontWeight={'bold'}>Become a gut health expert </Text>
                <Text fontWeight={'medium'}>
                  by learning from top clinicians
                </Text>
              </ListItem>
            </ul>
          </Box>
          {!!affiliate?.faq_link && (
            <Text fontSize={17}>
              Questions? Read Ayble's{' '}
              <Link
                isExternal
                _text={{ textTransform: 'none' }}
                href={affiliate?.faq_link}
              >
                FAQs
              </Link>
            </Text>
          )}
        </Box>
      </VStack>
    );
  };

  return (
    <Flex direction={flexDir} space={1} height="100%" width="100%">
      <Intro />
      <OnboardingIntroMedia />
      <LoginModal showLogin={showLogin} setShowLogin={setShowLogin} />
    </Flex>
  );
}

export default EnterpriseSignupIntro;
